import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { withTax } from '../../taxUtil';
import { displayName } from '../../userDisplay';
import { formatCurrency } from '../../currencyUtil';
import { GradeBadge } from '../../components/CarForm';

const STATUS = {
  pending:  { cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-800',       label: 'Pending'  },
  accepted: { cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-800', label: 'Accepted' },
  rejected: { cls: 'bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800',                   label: 'Rejected' },
};

const DEAL_STATUS = {
  draft:    { color: 'text-amber-600 dark:text-amber-400',     dot: 'bg-amber-400',   label: 'Draft' },
  sent:     { color: 'text-blue-600 dark:text-blue-400',       dot: 'bg-blue-500',    label: 'Sent' },
  complete: { color: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Complete' },
  archived: { color: 'text-slate-500 dark:text-slate-400',     dot: 'bg-slate-400',   label: 'Archived' },
};

function CarPhoto({ photos }) {
  if (photos?.[0]) return <img src={photos[0]} alt="" className="w-full h-full object-cover" />;
  return (
    <svg className="w-8 h-8 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
        d="M8 17l-2-2m0 0l-2-2m2 2l2-2m-2 2V9m10 2l2 2m0 0l2 2m-2-2l-2 2m2-2V9M3 9a2 2 0 012-2h14a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    </svg>
  );
}

export default function BidReview({ onPendingChange }) {
  const [bids, setBids]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('all');
  const [counterAmounts, setCounterAmounts] = useState({});
  const [countering, setCountering]         = useState({});
  const [actionErrors, setActionErrors]     = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get('/bids')
      .then(d  => { if (mounted) setBids(d); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const updateStatus = async (bid, status) => {
    setActionErrors(e => ({ ...e, [bid.id]: null }));
    try {
      await api.put(`/bids/${bid.id}`, { status });
      setBids(prev => prev.map(x => {
        if (x.id === bid.id) return { ...x, status };
        // When accepting, the backend rejects all other pending bids on the same car
        if (status === 'accepted' && x.car_id === bid.car_id && x.status === 'pending')
          return { ...x, status: 'rejected' };
        return x;
      }));
    } catch (err) {
      setActionErrors(e => ({ ...e, [bid.id]: err.message }));
    }
  };

  const submitCounter = async (bid) => {
    const price = counterAmounts[bid.id];
    if (!price) return;
    setCountering(c => ({ ...c, [bid.id]: true }));
    setActionErrors(e => ({ ...e, [bid.id]: null }));
    try {
      const updated = await api.post(`/bids/${bid.id}/counter`, { price });
      setBids(prev => prev.map(x => x.id === bid.id ? { ...x, ...updated } : x));
      setCounterAmounts(a => ({ ...a, [bid.id]: '' }));
    } catch (err) {
      setActionErrors(e => ({ ...e, [bid.id]: err.message }));
    } finally {
      setCountering(c => ({ ...c, [bid.id]: false }));
    }
  };

  const pending = bids.filter(b => b.status === 'pending').length;
  useEffect(() => { onPendingChange?.(pending); }, [pending]); // eslint-disable-line

  // Apply status filter then group by deal, then by car within each deal
  const STATUS_ORDER = { pending: 0, accepted: 1, rejected: 2 };
  const visible = filter === 'all' ? bids : bids.filter(b => b.status === filter);

  const dealMap = new Map();
  for (const bid of visible) {
    if (!dealMap.has(bid.deal_id)) dealMap.set(bid.deal_id, { deal: bid, carMap: new Map() });
    const { carMap } = dealMap.get(bid.deal_id);
    if (!carMap.has(bid.car_id)) carMap.set(bid.car_id, { car: bid, bids: [] });
    carMap.get(bid.car_id).bids.push(bid);
  }
  // Sort bids within each car: by status, then highest price first
  for (const { carMap } of dealMap.values()) {
    for (const group of carMap.values()) {
      group.bids.sort((a, b) => {
        const sd = (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3);
        return sd !== 0 ? sd : Number(b.bid_price) - Number(a.bid_price);
      });
    }
  }
  // Sort cars within each deal: most pending bids first, then by car_id
  // Sort deals: most pending bids first, then by deal_id
  const dealGroups = [...dealMap.values()].map(({ deal, carMap }) => {
    const carGroups = [...carMap.values()].sort((a, b) => {
      const ap = a.bids.filter(x => x.status === 'pending').length;
      const bp = b.bids.filter(x => x.status === 'pending').length;
      return bp - ap || a.car.car_id - b.car.car_id;
    });
    const pendingCount = carGroups.reduce((sum, g) => sum + g.bids.filter(x => x.status === 'pending').length, 0);
    return { deal, carGroups, pendingCount };
  }).sort((a, b) => b.pendingCount - a.pendingCount || a.deal.deal_id - b.deal.deal_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{error}</div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          {pending > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
              {pending} bid{pending !== 1 ? 's' : ''} awaiting review
            </p>
          )}
        </div>
        {bids.length > 0 && (
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {['all', 'pending', 'accepted', 'rejected'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${
                  filter === f
                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}>
                {f}
              </button>
            ))}
          </div>
        )}
      </div>

      {!bids.length ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">No bids yet</h3>
          <p className="text-sm text-slate-400 dark:text-slate-500">Bids will appear here once buyers respond to your deals.</p>
        </div>
      ) : dealGroups.length === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-500 py-12 text-center">No {filter} bids.</p>
      ) : (
        <div className="space-y-6">
          {dealGroups.map(({ deal, carGroups, pendingCount }) => {
            const ds = DEAL_STATUS[deal.deal_status] || DEAL_STATUS.draft;
            return (
              <div key={deal.deal_id} className="card overflow-hidden">

                {/* Deal header */}
                <div className="flex items-center gap-3 px-5 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => navigate(`/seller/deals/${deal.deal_uuid}`)}
                    className="font-semibold text-slate-800 dark:text-slate-100 hover:underline truncate min-w-0"
                  >
                    {deal.deal_name || `Deal #${deal.deal_id}`}
                  </button>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium shrink-0 ${ds.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${ds.dot} shrink-0`} />
                    {ds.label}
                  </span>
                  <div className="flex-1" />
                  {pendingCount > 0 && (
                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 shrink-0">
                      {pendingCount} pending
                    </span>
                  )}
                </div>

                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {carGroups.map(({ car, bids: carBids }) => (
                    <div key={car.car_id}>

                      {/* Car header */}
                      <div className="flex flex-wrap sm:flex-nowrap items-center gap-4 px-5 py-4 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                        <div className="w-20 h-14 bg-slate-200 dark:bg-slate-700 rounded-lg shrink-0 overflow-hidden flex items-center justify-center">
                          <CarPhoto photos={car.photos_videos} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 dark:text-slate-100 leading-tight">
                            {[car.year, car.make, car.model, car.trim].filter(Boolean).join(' ') || car.vin || `Car #${car.car_id}`}
                          </p>
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                            {car.vin || '—'} &middot; {car.mileage ? `${Number(car.mileage).toLocaleString()} mi` : '—'}
                          </p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <GradeBadge grade={car.grade} />
                            {car.operational === false && (
                              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800">
                                Inoperable
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-6 shrink-0 text-right ml-auto">
                          <div>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mb-0.5">Reserve (w/ tax)</p>
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                              {car.reserve_price ? `$${formatCurrency(withTax(car.reserve_price, car.tax_rate))}` : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mb-0.5">Buy Now (w/ tax)</p>
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                              {car.buy_now_price ? `$${formatCurrency(withTax(car.buy_now_price, car.tax_rate))}` : '—'}
                            </p>
                          </div>
                          <div className="pl-2 border-l border-slate-200 dark:border-slate-600">
                            <p className="text-xs text-slate-400 dark:text-slate-500 mb-0.5">Bids</p>
                            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{carBids.length}</p>
                          </div>
                        </div>
                      </div>

                      {/* Bid rows */}
                      <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
                        {carBids.map(bid => {
                          const s = STATUS[bid.status] || STATUS.pending;
                          const buyerName = displayName({ fname: bid.buyer_fname, lname: bid.buyer_lname, id: bid.buyer_id });
                          const awaitingBuyer = bid.status === 'pending' && bid.last_offer_by === 'seller';
                          const history = bid.offer_history || [];
                          return (
                            <div key={bid.id} className="flex flex-wrap sm:flex-nowrap items-start gap-4 px-5 py-3">
                              <div className="basis-full sm:basis-auto flex-1 min-w-0 pt-0.5">
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{buyerName}</p>
                                {bid.buyer_account_name && (
                                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{bid.buyer_account_name}</p>
                                )}
                                {history.length > 1 && (
                                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 truncate">
                                    {history.map((o, i) => (
                                      <React.Fragment key={i}>
                                        {i > 0 && ' → '}
                                        <span className={o.offered_by === 'seller' ? 'text-blue-500 dark:text-blue-400 font-medium' : ''}>
                                          ${formatCurrency(o.price)}
                                        </span>
                                      </React.Fragment>
                                    ))}
                                  </p>
                                )}
                                {actionErrors[bid.id] && (
                                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{actionErrors[bid.id]}</p>
                                )}
                              </div>

                              {bid.bid_type === 'buy_now' && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-800 shrink-0 mt-0.5">
                                  Buy Now
                                </span>
                              )}

                              <p className="text-base font-bold tabular-nums text-blue-600 dark:text-blue-400 shrink-0 w-24 text-right pt-0.5">
                                ${formatCurrency(bid.bid_price)}
                              </p>

                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 w-20 text-center mt-0.5 ${s.cls}`}>
                                {s.label}
                              </span>

                              <div className="w-full sm:w-48 shrink-0 flex flex-col items-end gap-1.5">
                                {bid.status === 'pending' ? (
                                  awaitingBuyer ? (
                                    <span className="text-xs text-slate-400 dark:text-slate-500 italic">Awaiting buyer response</span>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <button onClick={() => updateStatus(bid, 'rejected')}
                                          className="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-red-300 hover:text-red-600 hover:bg-red-50 dark:hover:border-red-700 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors">
                                          Reject
                                        </button>
                                        <button onClick={() => updateStatus(bid, 'accepted')}
                                          className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">
                                          Accept
                                        </button>
                                      </div>
                                      {bid.bid_type === 'custom' && (
                                        <div className="flex items-center border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 transition-shadow">
                                          <span className="pl-2.5 text-slate-400 text-xs">$</span>
                                          <input
                                            type="number"
                                            placeholder="Counter"
                                            value={counterAmounts[bid.id] || ''}
                                            onChange={e => setCounterAmounts(a => ({ ...a, [bid.id]: e.target.value }))}
                                            className="w-20 px-1.5 py-1.5 text-xs bg-transparent text-slate-800 dark:text-slate-200 focus:outline-none"
                                          />
                                          <button
                                            onClick={() => submitCounter(bid)}
                                            disabled={!counterAmounts[bid.id] || countering[bid.id]}
                                            className="px-2 py-1.5 text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 border-l border-slate-200 dark:border-slate-600 transition-colors disabled:opacity-40"
                                          >
                                            Counter
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )
                                ) : bid.status === 'rejected' && (
                                  <button onClick={() => updateStatus(bid, 'pending')}
                                    className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                                    Undo
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                    </div>
                  ))}
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
